import ResetIcon from '@/assets/all-icons/reset-icon';
import SaveArtworkIcon from '@/assets/all-icons/save-artwork-icon';
import useTranslations from '@/i18n/useTranslations';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';

import { GUIDANCE_COLOR, modes } from '../constants';

export default function CanvasPanel({
  canvasRef,
  canvasContainerRef,
  mode,
  setMode,
  cursorPosition,
  inspirationTopic,
  onShuffleTopic,
  saveToast,
  stabilizerEnabled,
  toggleStabilizer,
  hasStroke,
  isCurrentPieceSaved,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  onReset,
  onSave,
}) {
  const t = useTranslations('common.newspaper.art_games.ippitsugaki.canvas');

  return (
    <div className="ippitsugaki__canvas-panel">
      <div className="ippitsugaki__canvas-header">
        <div className="ippitsugaki__mode-selector" role="tablist" aria-label={t('aria.game_modes')}>
          {modes.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`ippitsugaki__mode-chip ${mode === item.id ? 'is-active' : ''}`}
              onClick={() => setMode(item.id)}
              role="tab"
              aria-selected={mode === item.id}
              aria-label={t(`modes.${item.id}.label`)}
            >
              <span className={`ippitsugaki__mode-label${item.id === 'inspiration' ? ' ippitsugaki__mode-label--full' : ''}`}>
                {t(`modes.${item.id}.label`)}
              </span>
              {item.id === 'inspiration' && (
                <span className="ippitsugaki__mode-label ippitsugaki__mode-label--short" aria-hidden="true">
                  {t('modes.inspiration.short_label')}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="ippitsugaki__action-group">
          <ContinueButton type="button" className="ippitsugaki__action ippitsugaki__action--secondary" onClick={onReset}>
            {t('actions.reset')}
          </ContinueButton>
          <ContinueButton
            type="button"
            className="ippitsugaki__action ippitsugaki__action--primary"
            onClick={onSave}
            disabled={!hasStroke}
          >
            {t('actions.save')}
          </ContinueButton>
        </div>
      </div>
      <div className="ippitsugaki__canvas-stage">
        <div className="ippitsugaki__canvas-wrapper" ref={canvasContainerRef}>
          <canvas
            ref={canvasRef}
            className="ippitsugaki__canvas"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            onPointerLeave={onPointerUp}
            aria-label={t('aria.canvas')}
          />
          {mode === 'inspiration' && !hasStroke && !cursorPosition && (
            <p className="ippitsugaki__canvas-instruction">{t('hint')}</p>
          )}
          {cursorPosition && (
            <span
              className="ippitsugaki__guidance"
              style={{
                transform: `translate3d(${cursorPosition.x}px, ${cursorPosition.y}px, 0)`,
                '--guidance-color': GUIDANCE_COLOR,
              }}
            />
          )}
          {saveToast && <div className="ippitsugaki__toast">{saveToast}</div>}
        </div>
      </div>
      <div className="ippitsugaki__canvas-footer">
        <div className="ippitsugaki__footer-controls">
          <div className="ippitsugaki__stabilizer">
            <span className="ippitsugaki__stabilizer-label ippitsugaki__stabilizer-label--desktop">{t('stabilizer.label')}</span>
            <span className="ippitsugaki__stabilizer-label ippitsugaki__stabilizer-label--mobile">{t('stabilizer.short_label')}</span>
            <button
              type="button"
              className={`ippitsugaki__toggle ${stabilizerEnabled ? 'is-on' : ''}`}
              onClick={toggleStabilizer}
              aria-pressed={stabilizerEnabled}
            >
              <span className="ippitsugaki__toggle-handle" />
            </button>
          </div>
          <div className="ippitsugaki__mobile-actions">
            <button
              type="button"
              className="ippitsugaki__icon-action"
              onClick={onReset}
              aria-label={t('actions.reset')}
            >
              <ResetIcon className="ippitsugaki__icon-action-icon ippitsugaki__icon-action-icon--reset" />
            </button>
            <button
              type="button"
              className={`ippitsugaki__icon-action${isCurrentPieceSaved ? ' is-active' : ''}`}
              onClick={onSave}
              disabled={!hasStroke}
              aria-label={t('actions.save')}
            >
              <SaveArtworkIcon className={`ippitsugaki__icon-action-icon ippitsugaki__icon-action-icon--save${isCurrentPieceSaved ? ' fill' : ''}`} />
            </button>
          </div>
        </div>
        <div className="ippitsugaki__footer-message">
          {mode === 'inspiration' && inspirationTopic ? (
            <div className="ippitsugaki__prompt-display" aria-live="polite">
              <span className="ippitsugaki__prompt-label">{t('prompt.label')}</span>
              <strong className="ippitsugaki__prompt-topic">{inspirationTopic}</strong>
              <button type="button" className="ippitsugaki__prompt-shuffle" onClick={onShuffleTopic}>
                {t('prompt.shuffle')}
              </button>
            </div>
          ) : (
            <p className="ippitsugaki__hint">{t('hint')}</p>
          )}
        </div>
      </div>
    </div>
  );
}
